import { conversionToTreeViewItem } from "@helpers/generals";
import { baseApi } from "./baseApi";
import {
    BalanceSheet,
    CashFlow,
    CustomerSupplier,
    CustomerSupplierInvoice,
    Expenditure,
    PalencaData,
    ProductsAndServices,
    ProductService,
    Sale,
} from "@interfaces/syntage";
import {
    CreditBureauInfo,
    CreditBureauInfoRecord,
    CreditDetailRecord,
    InvoicesResponse,
    KPIsModel,
    MatrixRecord,
    ModelDownloadStatus,
    MopStats,
    PaymentCapacity,
    PaymentCapacitySubResponse,
    RpcInfo,
    RugInfo,
} from "@interfaces/model";

import { UUID } from "crypto";
import { Response, ResponsePagination } from "@interfaces/response";
import { ClientSuppliers } from "@interfaces/references";

const satCore = baseApi.injectEndpoints({
    endpoints: (build) => ({
        postMonthlySales: build.mutation<Array<Sale>, { rfc: string; startDate: string; endDate: string }>({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/monthly-total-sales-revenue",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<Array<Sale>>) => {
                return response.data;
            },
        }),
        postMonthlyExpenditures: build.mutation<
            Array<Expenditure>,
            { rfc: string; startDate: string; endDate: string }
        >({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/monthly-total-expenditures",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<Array<Expenditure>>) => {
                return response.data;
            },
        }),
        postMonthlyCashFlow: build.mutation<Array<CashFlow>, { rfc: string; startDate: string; endDate: string }>({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/monthly-total-cash-flow",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<Array<CashFlow>>) => {
                return response.data;
            },
        }),
        postMonthlyCustomerSupplierInvoice: build.mutation<
            { clients: Array<CustomerSupplier>; suppliers: Array<CustomerSupplier> },
            { rfc: string; startDate: string; endDate: string }
        >({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/customer-supplier-invoice-concentration",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<CustomerSupplierInvoice>) => {
                return {
                    clients: response.data.clients ?? [],
                    suppliers: response.data.suppliers ?? [],
                };
            },
        }),
        postMonthlyProductsAnsServices: build.mutation<
            {
                bought: Array<ProductService>;
                sold: Array<ProductService>;
            },
            { rfc: string; startDate: string; endDate: string }
        >({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/products-and-services",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<ProductsAndServices>) => {
                return {
                    bought: response.data.bought ?? [],
                    sold: response.data.sold ?? [],
                };
                return response.data;
            },
        }),
        postBalanceSheet: build.mutation<Array<any>, { rfc: string; startDate: string; endDate: string }>({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/balance-sheet",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<BalanceSheet>) => {
                if (Array.isArray(response.data) || response.data.data?.length === 0) {
                    return [];
                }

                return response.data.data.map((item) => conversionToTreeViewItem(item, 0));
            },
        }),
        postIncomeStatement: build.mutation<Array<any>, { rfc: string; startDate: string; endDate: string }>({
            query: ({ rfc, startDate, endDate }) => ({
                url: "/sat_core/dashboards/income-statement",
                method: "POST",
                body: {
                    rfc,
                    date_from: startDate,
                    date_to: endDate,
                },
            }),
            transformResponse: (response: Response<Array<any>>) => {
                return response.data.data.map((item) => conversionToTreeViewItem(item, 0));
            },
        }),
        postModelDownloadStatus: build.mutation<ModelDownloadStatus, { applicationId: UUID; rfc: string }>({
            query: ({ applicationId, rfc }) => ({
                url: "/sat_core/dashboards/model-status",
                method: "POST",
                body: {
                    rfc: rfc,
                    loan_application_id: applicationId,
                },
            }),
            transformResponse: (response: Response<ModelDownloadStatus>) => {
                return response.data;
            },
        }),
        postRugInfo: build.mutation<Array<RugInfo>, { loanApplicationId: UUID; rfc: string }>({
            query: (payload) => ({
                url: "/sat_core/dashboards/rug-info",
                method: "POST",
                body: {
                    loan_application_id: payload.loanApplicationId,
                    rfc: payload.rfc,
                },
            }),
            transformResponse: (response: Response<Array<RugInfo>>) => {
                return response.data;
            },
        }),
        postRpcInfo: build.mutation<Response<RpcInfo>, string>({
            query: (rfc) => ({
                url: "/sat_core/dashboards/rpc-info",
                method: "POST",
                body: {
                    rfc: rfc,
                },
            }),
        }),
        getActFile: build.query<Response<string>, UUID>({
            query: (fileId) => ({
                url: `/sat_core/dashboards/syntage-file/${fileId}`,
            }),
        }),
        postRetryRunModel: build.mutation<void, any>({
            query: (body) => ({
                url: `/sat_core/run_model`,
                method: "POST",
                body: body,
            }),
        }),
        postRfcInvoices: build.mutation<void, any>({
            query: (body) => ({
                url: "/sat_core/update_rfc_invoices",
                method: "POST",
                body: body,
            }),
        }),
        postCreditBureauReconsultAuth: build.mutation<void, any>({
            query: (body) => ({
                url: "/sat_core/credit_bureau_reconsult_authorization",
                method: "POST",
                body: body,
            }),
        }),
        postPalenca: build.mutation<PalencaData, UUID>({
            query: (loanApplicationId) => ({
                url: "/sat_core/buro_ingresos",
                method: "POST",
                body: {
                    loan_application_id: loanApplicationId,
                },
            }),
            transformResponse: (response: Response<PalencaData>) => {
                return response.data;
            },
        }),
        getClientReferences: build.query<Array<ClientSuppliers>, string>({
            query: (params) => ({
                url: `/sat_core/clients_and_suppliers?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ClientSuppliers>) => {
                return response.data.data;
            },
        }),
        postPaymentCapacity: build.mutation<PaymentCapacity, { applicationId: UUID; clientRfc: string }>({
            query: (body) => ({
                url: "/sat_core/payment_capacity",
                method: "POST",
                body: {
                    loan_application_id: body.applicationId,
                    client_rfc: body.clientRfc,
                },
            }),
            transformResponse: (response: Response<PaymentCapacitySubResponse>) => {
                return response.data.data;
            },
        }),
        getTaxKpis: build.query<Array<KPIsModel>, string>({
            query: (params) => ({
                url: `/sat_core/dashboards/tax-return-kpis?${params}`,
            }),
            transformResponse: (response: Response<Array<KPIsModel>>) => {
                return response.data;
            },
        }),
        getInvoiceDetails: build.query<InvoicesResponse, string>({
            query: (params) => ({
                url: `/sat_core/dashboards/invoices?${params}`,
            }),
            transformResponse: (response: Response<InvoicesResponse>) => {
                return response.data;
            },
        }),
        getInvoiceFile: build.query<Response<any>, UUID>({
            query: (fileId) => ({
                url: `/sat_core/dashboards/invoice-file/${fileId}`,
            }),
        }),
        // old_getCreditBureauInfo: build.query<Array<CreditBureauInfoRecord>, { applicationId: UUID; params: string }>({
        //     query: ({ applicationId, params }) => ({
        //         url: `/sat_core/bureau_information/${applicationId}?${params}`,
        //     }),
        //     transformResponse: (response: ResponsePagination<CreditBureauInfoRecord>) => {
        //         return response.data.data;
        //     },
        // }),
        getCreditBureauGeneralData: build.query<CreditBureauInfo, string>({
            query: (params) => ({
                url: `/sat_core/bureau/general_data?${params}`,
            }),
            transformResponse: (response: Response<CreditBureauInfo>) => {
                return response.data;
            },
        }),
        getCreditBureauCountCurrentCredits: build.query<Record<string, number>, string>({
            query: (params) => ({
                url: `/sat_core/bureau/count_current_credits?${params}`,
            }),
            transformResponse: (response: Response<Record<string, number>>) => {
                return response.data;
            },
        }),
        getCreditBureauMopsPercentage: build.query<Record<string, MopStats>, string>({
            query: (params) => ({
                url: `/sat_core/bureau/mops_percentage?${params}`,
            }),
            transformResponse: (response: Response<Record<string, MopStats>>) => {
                return response.data;
            },
        }),
        getCreditBureauMopsMetrics: build.query<Record<string, Record<string, MopStats>>, string>({
            query: (params) => ({
                url: `/sat_core/bureau/mops_metrics?${params}`,
            }),
            transformResponse: (response: Response<Record<string, Record<string, MopStats>>>) => {
                return response.data;
            },
        }),
        getCreditBureauMatrix: build.query<Array<CreditDetailRecord>, string>({
            query: (params) => ({
                url: `/sat_core/bureau/matrix?${params}`,
            }),
            transformResponse: (response: Response<Array<MatrixRecord>>) => {
                return response.data.map((record) => ({
                    contract: record.Contrato,
                    lenderType: record["Tipo Otorgante"],
                    currency: record.Moneda,
                    status: record.Status,
                    type: record["Tipo Credito"],
                    code: record["Codigo credito"],
                    termVsRevolving: record["Simple vs Revolvente"],
                    detail: record.Detalle,
                    openingDate: record.Apertura,
                    closingDate: record.Cierre,
                    term: record.Plazo,
                    originalAmount: record.Original,
                    currentAmount: record.Vigente,
                    lastUpdatedPeriod: record["Ultimo Periodo Actualizado"],
                    history: record.Historicos,
                }));
            },
        }),
    }),
});

export const {
    usePostMonthlySalesMutation,
    usePostMonthlyExpendituresMutation,
    usePostMonthlyCashFlowMutation,
    usePostMonthlyCustomerSupplierInvoiceMutation,
    usePostMonthlyProductsAnsServicesMutation,
    usePostBalanceSheetMutation,
    usePostIncomeStatementMutation,
    usePostModelDownloadStatusMutation,
    usePostRugInfoMutation,
    usePostRpcInfoMutation,
    useLazyGetActFileQuery,
    usePostRetryRunModelMutation,
    usePostRfcInvoicesMutation,
    usePostCreditBureauReconsultAuthMutation,
    usePostPalencaMutation,
    useLazyGetClientReferencesQuery,
    useGetClientReferencesQuery,
    usePostPaymentCapacityMutation,
    useLazyGetTaxKpisQuery,
    useLazyGetInvoiceDetailsQuery,
    useLazyGetInvoiceFileQuery,
    // old_useLazyGetCreditBureauInfoQuery,
    useLazyGetCreditBureauGeneralDataQuery,
    useLazyGetCreditBureauCountCurrentCreditsQuery,
    useLazyGetCreditBureauMopsMetricsQuery,
    useLazyGetCreditBureauMopsPercentageQuery,
    useLazyGetCreditBureauMatrixQuery,
} = satCore;
